import type { StateCreator } from 'zustand'

import { getSafeImageUrl } from '@common/core/helpers'
import { useGatewaysStore } from '@common/modules/gateways/stores'
import { useProjectsStore } from '@common/modules/projects/stores'
import { getCurrentProject } from '@common/modules/projects/stores/projects.actions'
import { updateWorkspaceContext } from '@common/modules/user'
import { setLastUsedWorkspaceIdOnCurrentUser } from '@common/modules/user/utils/setLastUsedWorkspaceIdOnCurrentUser'
import {
  deleteWorkspace as deleteWorkspaceApi,
  getWorkspacesByUserId,
  updateWorkspaceLogo as updateWorkspaceLogoApi,
  updateWorkspaceName as updateWorkspaceNameApi,
} from '@common/modules/workspaces'
import { useWorkspacesStore } from '@common/modules/workspaces/stores'

import { workspaceToSettings } from '../../objects'
import type { WorkspaceStore } from '../types'

import type { WorkspaceGeneralActionsSlice } from './workspace-general-actions.slice.type'

function getNextWorkspaceIndex(currentIndex: number, remainingCount: number) {
  if (remainingCount === 0) return -1
  if (currentIndex < 0) return 0

  return Math.min(currentIndex, remainingCount - 1)
}

export const createWorkspaceGeneralActionsSlice: StateCreator<
  WorkspaceStore,
  [['zustand/immer', never]],
  [],
  WorkspaceGeneralActionsSlice
> = (set, get) => ({
  getWorkspaceSettings: () => {
    const { currentWorkspace } = useWorkspacesStore.getState()
    const projectCount = useProjectsStore.getState().projects.length
    const workspace = currentWorkspace ? workspaceToSettings(currentWorkspace) : null

    set((state: WorkspaceStore) => {
      state.workspace = workspace ? { ...workspace, projectCount } : workspace
    })
  },

  updateWorkspaceName: async (name: string) => {
    const workspaceId = get().workspace?.id
    if (!workspaceId) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      await updateWorkspaceNameApi(workspaceId, name)

      const workspacesStore = useWorkspacesStore.getState()
      const updatedWorkspaces = workspacesStore.workspaces.map((workspace) =>
        workspace.id === workspaceId ? { ...workspace, name } : workspace
      )
      const updatedCurrentWorkspace =
        workspacesStore.currentWorkspace?.id === workspaceId
          ? { ...workspacesStore.currentWorkspace, name }
          : workspacesStore.currentWorkspace

      workspacesStore.setState({
        workspaces: updatedWorkspaces,
        currentWorkspace: updatedCurrentWorkspace,
      })

      set((state: WorkspaceStore) => {
        if (state.workspace) {
          state.workspace.name = name
        }
      })
    } catch (err) {
      set((state: WorkspaceStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update workspace name'
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  updateWorkspaceLogo: async (logo: number[]) => {
    const workspaceId = get().workspace?.id
    if (!workspaceId) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      await updateWorkspaceLogoApi(workspaceId, logo)

      const latestWorkspaces = await getWorkspacesByUserId()
      const currentWorkspace = latestWorkspaces.find((item) => item.id === workspaceId) ?? null

      if (!currentWorkspace) {
        throw new Error('Workspace refresh failed after uploading image.')
      }

      if (!getSafeImageUrl(currentWorkspace.logoUrl)) {
        throw new Error('Workspace image could not be applied. Please try again.')
      }

      useWorkspacesStore.getState().setState({
        workspaces: latestWorkspaces,
        currentWorkspace,
      })

      set((state: WorkspaceStore) => {
        const { currentWorkspace: refreshed } = useWorkspacesStore.getState()
        state.workspace = refreshed
          ? {
              ...workspaceToSettings(refreshed),
              projectCount: useProjectsStore.getState().projects.length,
            }
          : null
      })
    } catch (err) {
      set((state: WorkspaceStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update workspace logo'
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  deleteWorkspace: async () => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      await deleteWorkspaceApi(workspace.id)

      const workspacesStore = useWorkspacesStore.getState()
      const projectsStore = useProjectsStore.getState()
      const deletedWorkspaceIndex = workspacesStore.workspaces.findIndex((item) => item.id === workspace.id)
      const remainingWorkspaces = workspacesStore.workspaces.filter((item) => item.id !== workspace.id)
      const nextWorkspaceIndex = getNextWorkspaceIndex(deletedWorkspaceIndex, remainingWorkspaces.length)
      const nextWorkspace = nextWorkspaceIndex >= 0 ? remainingWorkspaces[nextWorkspaceIndex] : null

      workspacesStore.setState({
        workspaces: remainingWorkspaces,
        currentWorkspace: nextWorkspace,
      })

      useGatewaysStore.getState().setState({
        gateways: [],
        lastFetchedProjectId: null,
        loading: false,
        error: null,
      })

      projectsStore.invalidateProjectsCache()

      if (nextWorkspace) {
        setLastUsedWorkspaceIdOnCurrentUser(nextWorkspace.id)
        await updateWorkspaceContext(nextWorkspace.id)
        await getCurrentProject(nextWorkspace.id)

        const { projects } = useProjectsStore.getState()

        set((state: WorkspaceStore) => {
          const { currentWorkspace: refreshed } = useWorkspacesStore.getState()
          state.workspace = refreshed ? { ...workspaceToSettings(refreshed), projectCount: projects.length } : null
        })
      } else {
        set((state: WorkspaceStore) => {
          state.workspace = null
        })
      }
    } catch (err) {
      set((state: WorkspaceStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to delete workspace'
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  clearError: () => {
    set((state: WorkspaceStore) => {
      state.error = null
    })
  },
})
